import { Suspense, lazy, type ComponentType } from "react";
import { createBrowserRouter, Outlet, type RouteObject } from "react-router";
import { AuthLayout } from "@/layouts/AuthLayout";
import { StormLandingPage } from "@/pages/storm/StormLandingPage";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { RedirectIfAuthenticated } from "@/components/auth/RedirectIfAuthenticated";

// Only /go (the Facebook ad landing page) and the auth shell load up front.
// Everything else, public pages included, is fetched when first visited, so a
// visitor from an ad does not download and parse the whole site before the
// offer paints. Nothing imported eagerly here may reach @/lib/supabase/client:
// that module pulls in supabase-js, which /go does not use.
function lazyNamed<M, K extends keyof M>(load: () => Promise<M>, name: K) {
  return lazy(async () => ({ default: (await load())[name] as ComponentType }));
}

const SiteLayout = lazyNamed(() => import("@/layouts/SiteLayout"), "SiteLayout");
const AppLayout = lazyNamed(() => import("@/layouts/AppLayout"), "AppLayout");
const ChannelLayout = lazyNamed(() => import("@/layouts/ChannelLayout"), "ChannelLayout");
const AdminLayout = lazyNamed(() => import("@/layouts/AdminLayout"), "AdminLayout");
const RequireAdmin = lazyNamed(() => import("@/components/auth/RequireAdmin"), "RequireAdmin");
const RequireAdminOr404 = lazyNamed(() => import("@/components/auth/RequireAdminOr404"), "RequireAdminOr404");
const RequireOperatorOr404 = lazyNamed(() => import("@/components/grail-quests/RequireOperatorOr404"), "RequireOperatorOr404");
const GrailLayout = lazyNamed(() => import("@/components/grail-quests/GrailLayout"), "GrailLayout");

// Public pages
const HomePage = lazyNamed(() => import("@/pages/HomePage"), "HomePage");
const SubscribePage = lazyNamed(() => import("@/pages/SubscribePage"), "SubscribePage");
const NotFoundPage = lazyNamed(() => import("@/pages/NotFoundPage"), "NotFoundPage");
const DisclaimerPage = lazyNamed(() => import("@/pages/legal/DisclaimerPage"), "DisclaimerPage");
const PrivacyPolicyPage = lazyNamed(() => import("@/pages/legal/PrivacyPolicyPage"), "PrivacyPolicyPage");
const TermsOfServicePage = lazyNamed(() => import("@/pages/legal/TermsOfServicePage"), "TermsOfServicePage");
const QuizFlow = lazyNamed(() => import("@/components/QuizFlow"), "QuizFlow");
const FreedomCalculator = lazyNamed(() => import("@/tools/freedom-number/FreedomCalculator"), "FreedomCalculator");
const YieldOnCostTool = lazyNamed(() => import("@/tools/yield-on-cost/YieldOnCostTool"), "YieldOnCostTool");
const PaycheckPlanner = lazyNamed(() => import("@/tools/paycheck-planner/PaycheckPlanner"), "PaycheckPlanner");
const DividendVsGrowth = lazyNamed(() => import("@/tools/dividend-vs-growth/DividendVsGrowth"), "DividendVsGrowth");
const DripCalculator = lazyNamed(() => import("@/tools/drip-snowball/DripCalculator"), "DripCalculator");
const TaxDragCalculator = lazyNamed(() => import("@/tools/tax-drag/TaxDragCalculator"), "TaxDragCalculator");
const CutStressTest = lazyNamed(() => import("@/tools/cut-stress-test/CutStressTest"), "CutStressTest");

// Lazy-loaded premium pages
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const VerifyPage = lazy(() => import("@/pages/VerifyPage"));
const ExpiredPage = lazy(() => import("@/pages/ExpiredPage"));
const StocksPage = lazy(() => import("@/pages/StocksPage"));
const StockDetailPage = lazy(() => import("@/pages/StockDetailPage"));
const AttentionPage = lazy(() => import("@/pages/AttentionPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const OnboardingPage = lazy(() => import("@/pages/OnboardingPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const AdminUploadPage = lazy(() => import("@/pages/AdminUploadPage"));
const AdminSubscribersPage = lazy(() => import("@/pages/AdminSubscribersPage"));
const AdminMerlinsStudyPage = lazy(() => import("@/pages/AdminMerlinsStudyPage"));
const AdminToolCapturesPage = lazy(() => import("@/pages/AdminToolCapturesPage"));
const AdminPreviewsPage = lazy(() => import("@/pages/AdminPreviewsPage"));
const AdminPricesPage = lazy(() => import("@/pages/AdminPricesPage"));
// Token-addressed, deliberately OUTSIDE every auth guard: the colleague who
// collects the weekly preview is not a DR user and must not need an account.
const PreviewIssuePage = lazy(() => import("@/pages/PreviewIssuePage"));
const GrailQuestPage = lazy(() => import("@/pages/admin/grail-quests/QuestPage"));
const GrailWorkshopPage = lazy(() => import("@/pages/admin/grail-quests/WorkshopPage"));
const GrailSchedulePage = lazy(() => import("@/pages/admin/grail-quests/SchedulePage"));
const GrailRoundTablePage = lazy(() => import("@/pages/admin/grail-quests/RoundTablePage"));

// Community pages
const ChannelPage = lazy(() => import("@/pages/community/ChannelPage"));
const CommunityHomePage = lazy(() => import("@/pages/community/CommunityHomePage"));
const CommunityCategoryPage = lazy(() => import("@/pages/community/CommunityCategoryPage"));
const NewThreadPage = lazy(() => import("@/pages/community/NewThreadPage"));
const ThreadDetailPage = lazy(() => import("@/pages/community/ThreadDetailPage"));
const BookmarksPage = lazy(() => import("@/pages/community/BookmarksPage"));

function PageSuspense({ children }: { children: React.ReactNode }) {
  return (
    <div data-testid="page-suspense">
      <Suspense
        fallback={
          <div className="flex items-center justify-center min-h-screen">
            <div className="motion-safe:animate-pulse h-8 w-8 rounded-full bg-muted" />
          </div>
        }
      >
        {children}
      </Suspense>
    </div>
  );
}

const appRoutes: RouteObject[] = [
  // Facebook ad landing page. Outside SiteLayout on purpose: a visitor from an
  // ad gets one offer and the legal links, not the whole site navigation.
  { path: "/go", element: <StormLandingPage /> },
  // Public routes — SiteLayout, each page fetched on first visit
  {
    element: <SiteLayout />,
    children: [
      { path: "/", element: <HomePage /> },
      { path: "/quiz", element: <QuizFlow /> },
      { path: "/tools/freedom-number", element: <FreedomCalculator /> },
      { path: "/tools/yield-on-cost", element: <YieldOnCostTool /> },
      { path: "/tools/paycheck-planner", element: <PaycheckPlanner /> },
      { path: "/tools/dividend-vs-growth", element: <DividendVsGrowth /> },
      { path: "/tools/drip-snowball", element: <DripCalculator /> },
      { path: "/tools/tax-drag", element: <TaxDragCalculator /> },
      { path: "/tools/cut-stress-test", element: <CutStressTest /> },
      { path: "/subscribe", element: <SubscribePage /> },
      {
        path: "/preview/:token",
        element: (
          <PageSuspense>
            <PreviewIssuePage />
          </PageSuspense>
        ),
      },
      { path: "/disclaimer", element: <DisclaimerPage /> },
      { path: "/privacy", element: <PrivacyPolicyPage /> },
      { path: "/terms", element: <TermsOfServicePage /> },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
  // Auth routes — redirect if already logged in
  {
    element: <RedirectIfAuthenticated />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          {
            path: "/login",
            element: (
              <PageSuspense>
                <LoginPage />
              </PageSuspense>
            ),
          },
          {
            path: "/verify",
            element: (
              <PageSuspense>
                <VerifyPage />
              </PageSuspense>
            ),
          },
        ],
      },
    ],
  },
  // Expired — accessible with or without active subscription
  {
    element: <AuthLayout />,
    children: [
      {
        path: "/expired",
        element: (
          <PageSuspense>
            <ExpiredPage />
          </PageSuspense>
        ),
      },
    ],
  },
  // Grail Quests — deliberately outside RequireAuth/RequireAdmin. Anonymous
  // visitors, non-admins and (for the Workshop) non-operators get the NotFound
  // page at the same URL instead of a redirect (decisions D1, D2).
  {
    element: <RequireAdminOr404 />,
    children: [
      {
        element: <GrailLayout />,
        children: [
          {
            path: "/admin/grail-quests",
            element: (
              <PageSuspense>
                <GrailQuestPage />
              </PageSuspense>
            ),
          },
          {
            // Any admin: the operator and the owner read this one together.
            path: "/admin/grail-quests/schedule",
            element: (
              <PageSuspense>
                <GrailSchedulePage />
              </PageSuspense>
            ),
          },
          {
            // Any admin: Sir Leondegrance's suggestions are Roger's to act on.
            path: "/admin/grail-quests/round-table",
            element: (
              <PageSuspense>
                <GrailRoundTablePage />
              </PageSuspense>
            ),
          },
        ],
      },
      {
        element: <RequireOperatorOr404 />,
        children: [
          {
            element: <GrailLayout />,
            children: [
              {
                path: "/admin/grail-quests/workshop",
                element: (
                  <PageSuspense>
                    <GrailWorkshopPage />
                  </PageSuspense>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  // Premium routes — auth + active subscription required
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            path: "/stocks",
            element: (
              <PageSuspense>
                <StocksPage />
              </PageSuspense>
            ),
          },
          {
            path: "/stocks/:ticker",
            element: (
              <PageSuspense>
                <StockDetailPage />
              </PageSuspense>
            ),
          },
          {
            path: "/attention",
            element: (
              <PageSuspense>
                <AttentionPage />
              </PageSuspense>
            ),
          },
          {
            path: "/settings",
            element: (
              <PageSuspense>
                <SettingsPage />
              </PageSuspense>
            ),
          },
          {
            path: "/onboarding",
            element: (
              <PageSuspense>
                <OnboardingPage />
              </PageSuspense>
            ),
          },
          // Community routes — open to any active subscriber. Profile defaults
          // are seeded by the handle_new_user trigger so posting works immediately;
          // bio + tickers customization lives at /settings.
          // NOTE: the channel itself (/community) lives under ChannelLayout below
          // for its viewport-locked chat layout; these are the normal-scroll pages.
          {
            // Legacy thread feed — no longer linked from nav, kept alive so
            // older bookmarks/links don't 404. The default surface is the channel.
            path: "/community/threads",
            element: (
              <PageSuspense>
                <CommunityHomePage />
              </PageSuspense>
            ),
          },
          {
            path: "/community/c/:categorySlug",
            element: (
              <PageSuspense>
                <CommunityCategoryPage />
              </PageSuspense>
            ),
          },
          {
            path: "/community/threads/new",
            element: (
              <PageSuspense>
                <NewThreadPage />
              </PageSuspense>
            ),
          },
          {
            path: "/community/t/:slug",
            element: (
              <PageSuspense>
                <ThreadDetailPage />
              </PageSuspense>
            ),
          },
          {
            path: "/community/bookmarks",
            element: (
              <PageSuspense>
                <BookmarksPage />
              </PageSuspense>
            ),
          },
        ],
      },
      // The Round Table channel — viewport-locked chat layout (feed scrolls
      // internally, composer pinned). Separate from AppLayout's document scroll.
      {
        element: <ChannelLayout />,
        children: [
          {
            // No PageSuspense wrapper here: ChannelLayout owns the Suspense
            // boundary so ChannelView stays a direct child of the bounded <main>
            // and its h-full height chain isn't broken by an auto-height wrapper.
            path: "/community",
            element: <ChannelPage />,
          },
        ],
      },
      // Admin — requires auth + admin email
      {
        element: <RequireAdmin />,
        children: [
          {
            element: <AdminLayout />,
            children: [
              {
                path: "/admin",
                element: (
                  <PageSuspense>
                    <AdminPage />
                  </PageSuspense>
                ),
              },
              {
                path: "/admin/upload",
                element: (
                  <PageSuspense>
                    <AdminUploadPage />
                  </PageSuspense>
                ),
              },
              {
                path: "/admin/subscribers",
                element: (
                  <PageSuspense>
                    <AdminSubscribersPage />
                  </PageSuspense>
                ),
              },
              {
                path: "/admin/tool-captures",
                element: (
                  <PageSuspense>
                    <AdminToolCapturesPage />
                  </PageSuspense>
                ),
              },
              {
                path: "/admin/merlins-study",
                element: (
                  <PageSuspense>
                    <AdminMerlinsStudyPage />
                  </PageSuspense>
                ),
              },
              {
                path: "/admin/previews",
                element: (
                  <PageSuspense>
                    <AdminPreviewsPage />
                  </PageSuspense>
                ),
              },
              {
                path: "/admin/prices",
                element: (
                  <PageSuspense>
                    <AdminPricesPage />
                  </PageSuspense>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
];

// One boundary for every lazy layout, guard and page above that has no
// PageSuspense of its own. Navigations run in a transition, so moving between
// loaded pages keeps the current screen up while the next chunk arrives.
function RouteSuspense() {
  return (
    <Suspense fallback={null}>
      <Outlet />
    </Suspense>
  );
}

export const routes: RouteObject[] = [{ element: <RouteSuspense />, children: appRoutes }];

export const router = createBrowserRouter(routes);
